import React, { useEffect, useRef } from 'react';

import cx from 'classnames';
import { RadioGroup } from 'components';

import { DateTimeUnitEnum } from 'date-time-manipulations';
import { ScheduleReducerType } from 'lane-shared/domains/visitorManagement/reducers';
import { ScheduleType } from 'lane-shared/domains/visitorManagement/types/ScheduleType';
import { ScheduleTypeEnum } from 'lane-shared/domains/visitorManagement/types/VisitorManagementFeatureProperties';
import { useVisitorScheduleForm } from 'lane-shared/hooks';
import {
  VisitorManagementFeatureProperties,
} from 'lane-shared/types/features/VisitorManagementFeatureProperties';
import { useHandleVisitorSchedules } from './useHandleVisitorSchedules';
import ScheduleDateTime, {
  ScheduleDateTimeHandle,
} from '../ScheduleDateTime/ScheduleDateTime';
import ScheduleDateTimeRange from '../ScheduleDateTimeRange/ScheduleDateTimeRange';
import ScheduleMultiDateTime from '../ScheduleMultiDateTime/ScheduleMultiDateTime';
import ScheduleDateTimeRepeat from '../ScheduleRepeat/ScheduleDateTimeRepeat';

import styles from './VisitorScheduleForm.scss';

function ScheduleForm({
  className,
  contentClassName,
  properties,
  timeZone,
  value,
  submissionCompletedAt,
  onChange,
  isPreview = false,
}: {
  className?: string;
  contentClassName?: string;
  properties: VisitorManagementFeatureProperties;
  timeZone?: string;
  value: ScheduleType | null;
  submissionCompletedAt?: Date;
  onChange: (value: ScheduleType) => void;
  isPreview?: boolean;
}): React.ReactElement {
  const {
    options,
    isAllDay,
    startDate,
    endDate,
    dates,
    intervalType,
    endRepeatDate,
    intervalCount,
    weekdayRepeats,
    monthIntervalType,
    type,
    resetForm,
    handleDateTime,
    handleCustomDateTime,
    handleDateTimeRange,
    handleType,
    handleRepeat,
  } = useVisitorScheduleForm({
    properties,
    value,
    timeZone,
    onChange,
  });

  const dateTimeRef = useRef<ScheduleDateTimeHandle>(null);

  useEffect(() => {
    resetForm();

    if (dateTimeRef?.current) {
      dateTimeRef.current.reset();
    }
  }, [submissionCompletedAt]);

  const {
    isAllDayPassEnabled,
    minDate,
    maxDate,
    dateRangeMaximum,
  } = useHandleVisitorSchedules({
    timeZone,
    properties,
  });

  function renderScheduleType() {
    switch (type) {
      case ScheduleTypeEnum.SpecificDate:
        return (
          <ScheduleDateTime
            allDayPassEnabled={isAllDayPassEnabled}
            defaultIsAllDay={isAllDay}
            defaultStartDate={startDate}
            defaultEndDate={endDate}
            timeZone={timeZone}
            onChange={(value: ScheduleReducerType) => {
              handleDateTime(value);
            }}
            ref={dateTimeRef}
            minDate={minDate}
            maxDate={maxDate}
            isPreview={isPreview}
          />
        );
      case ScheduleTypeEnum.CustomDates:
        return (
          <ScheduleMultiDateTime
            allDayPassEnabled={isAllDayPassEnabled}
            defaultIsAllDay={isAllDay}
            defaultStartDate={startDate}
            defaultEndDate={endDate}
            defaultDates={dates}
            timeZone={timeZone}
            onChange={(value: ScheduleReducerType) => {
              handleCustomDateTime(value);
            }}
            ref={dateTimeRef}
            minDate={minDate}
            maxDate={maxDate}
            isPreview={isPreview}
          />
        );
      case ScheduleTypeEnum.DateRange:
        return (
          <ScheduleDateTimeRange
            allDayPassEnabled={isAllDayPassEnabled}
            defaultIsAllDay={isAllDay}
            defaultStartDate={startDate}
            defaultEndDate={endDate}
            timeZone={timeZone}
            onChange={(value: ScheduleReducerType) => {
              handleDateTimeRange(value);
            }}
            minDate={minDate}
            maxDate={maxDate}
            rangeMax={dateRangeMaximum}
            rangeUnit={DateTimeUnitEnum.Month}
            isPreview={isPreview}
          />
        );
      case ScheduleTypeEnum.Repeat:
        return (
          <ScheduleDateTimeRepeat
            allDayPassEnabled={isAllDayPassEnabled}
            defaultIsAllDay={isAllDay}
            defaultStartDate={startDate}
            defaultEndDate={endDate}
            defaultIntervalCount={intervalCount}
            defaultEndRepeatDate={endRepeatDate}
            defaultInterval={intervalType}
            defaultWeekdayRepeats={weekdayRepeats}
            defaultMonthIntervalType={monthIntervalType}
            timeZone={timeZone}
            onChange={(value: ScheduleReducerType) => {
              handleRepeat(value);
            }}
            minDate={minDate}
            maxDate={maxDate}
            isPreview={isPreview}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className={cx(styles.visitorScheduleForm, className)}>
      {options?.length > 1 && (
        <div className={styles.scheduleOptions}>
          <div className={styles.radioGroup}>
            <RadioGroup
              className={styles.radio}
              key="schedule"
              name="schedule"
              schema={{
                id: '_id',
                text: 'text',
                disabled: 'disabled',
              }}
              selected={type}
              items={options}
              onChange={value => handleType(value as ScheduleTypeEnum)}
              disabled={isPreview}
            />
          </div>
        </div>
      )}
      <div className={cx(styles.content, contentClassName)}>
        {renderScheduleType()}
      </div>
    </div>
  );
}

export default ScheduleForm;
